'use client';

import {
  createContext, ReactNode, useCallback, useContext, useMemo, useRef,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useStreamStateV2 } from '@components/templates/bia/hooks/useStreamStateV2';
import { useAnonSessionId } from '@hooks/common/useAnonSessionId';

interface BestieChatStateValue {
  /** Bestie chat stream — shared so it survives across the views that render the chat. */
  stream: ReturnType<typeof useStreamStateV2>['stream'];
  /** Submit a chat message to the Bestie stream. */
  submitChat: (text: string) => Promise<void>;
  /** Flag that the next chat view should open its input ready to type (e.g. when the
   *  user picks "coaching" from the prompt-bar menu). Lives here because ChatBranch
   *  remounts across the search↔chat mode switch, so a window event would race. */
  requestChatFocus: () => void;
  /** Read-and-clear the chat-focus request. Returns true once per request. */
  consumeChatFocusRequest: () => boolean;
}

const BestieChatStateContext = createContext<BestieChatStateValue | undefined>(undefined);

/**
 * @description Holds the shared live state for the Bestie candidate chat — the chat stream and
 * its submit handler. Mounted by the Bestie chat page so the stream + submit are available to
 * the prompt bar and the streamed-messages view.
 */
export const BestieChatStateProvider = ({ children, locale }: { children: ReactNode; locale: Locale }) => {
  const { stream, startStream } = useStreamStateV2(null);
  const anonSessionId = useAnonSessionId();

  const streamHeaders = useMemo(() => ({
    'x-anon-session-id': anonSessionId ?? '',
  }), [anonSessionId]);

  const submitChat = useCallback(async (text: string) => {
    const message = { id: uuidv4(), content: text, type: 'human' as const };
    await startStream(message, '', {
      headers: streamHeaders,
      configurations: { user_locale: locale || 'ro' },
    });
  }, [startStream, streamHeaders, locale]);

  // One-shot intent so a chat-view mount can auto-open its input. A ref (not state)
  // because it's consumed by an effect on the next mount, not rendered.
  const chatFocusRequestRef = useRef(false);
  const requestChatFocus = useCallback(() => { chatFocusRequestRef.current = true; }, []);
  const consumeChatFocusRequest = useCallback(() => {
    const requested = chatFocusRequestRef.current;
    chatFocusRequestRef.current = false;
    return requested;
  }, []);

  const value = useMemo(
    () => ({
      stream, submitChat, requestChatFocus, consumeChatFocusRequest,
    }),
    [stream, submitChat, requestChatFocus, consumeChatFocusRequest],
  );

  return <BestieChatStateContext.Provider value={value}>{children}</BestieChatStateContext.Provider>;
};

/** Access the MC shared chat stream + submit handler. Returns undefined outside the candidate shell. */
export const useBestieChatStateOptional = () => useContext(BestieChatStateContext);

export const useBestieChatState = () => {
  const ctx = useContext(BestieChatStateContext);
  if (!ctx) throw new Error('useBestieChatState must be used within BestieChatStateProvider');
  return ctx;
};
